/* global store ENVIRONMENT */
/* global FLUID_PLUS */
/* global IS_PREVIEW_PAGE */

import { ARTBOARD_BORDER_DOM_ID, initialiseAnimation } from 'Render/utils/importers';
import imagesLoaded from 'imagesloaded';
import createSpriteSlider from 'Renderer/utils/threeSixtyScript';
import createSwipeGallery from 'Renderer/utils/swipeGalleryScript';
import createImageZoom from 'Renderer/utils/imageZoomScript';
import createWipeAway from 'Renderer/utils/wipeAwayScript';
import createEffectLayer from 'Renderer/utils/effectLayerScript';
import { createButtonEvents } from 'Renderer/utils/buttonScript';
import {
  setRootElement,
  artboardRendered,
  selectActiveArtboard,
  setTimeline,
  resetTimeLine,
} from 'Renderer/actions/artboardActions';
import { initialiseComponentTimelines, getFilteredArtboardTimeline } from 'Renderer/domOperations/animationOperations';
import { maybeStartAd, visibilityChangeEvent } from 'Renderer/domOperations/adOperations';
import { handleKeyFrameActions } from 'Renderer/domOperations/actionOperations';
import { initialiseVideoActions, initialiseVideoControls } from 'Renderer/domOperations/videoOperations';
import { fireTracking } from 'Tracking/index';
import { REGEX_FOR_CACHEBUSTER, cachebusterFunction, replaceConsentMacros } from 'Tracking/utils';
import { AD_STATE_CHANGED } from 'Tracking/constants/events';
import { PARENT_FONT_SIZE_PX } from 'SharedConstants/renderer';
import { unescapeHtml, appendHtmlToElement } from 'Renderer/utils/';
import { resetScrollerAdContainer, resizeScrollerContainer } from 'Renderer/utils/scrollerContainer';
import { resizeRibbonContainer } from 'Renderer/utils/ribbonContainer';
import { resizeSkinsCentreContainer } from 'Renderer/utils/skinsContainer';
import { resizeWallpaperWrapper, resizePreviewWallpaperWrapper } from 'Renderer/utils/wallpaperContainer';
import { resizeOutstreamViewport } from 'Renderer/utils/outstreamContainer';
import { resizeWhiteSpaceOfParents } from 'Renderer/utils/maximiseWhiteSpace';
import { resizeOverlayContainer } from 'Renderer/utils/overlayContainer';
import { resizeIframe, pushdownIframe, resizePushdown } from 'Renderer/domOperations/iFrameOperations';
import { CLICK_THROUGH_BUTTON_CLASS, CLOSE_EXPAND_BUTTON_CLASS } from 'Tracking/constants/selectors';
import initialiseDocumentEventListeners from 'Tracking/eventListeners/document';
import { createSmartGroupEvents } from 'Renderer/utils/smartGroupScript';

export const runScript = (ivo, script) => {
  try {
  eval(script); // eslint-disable-line
  } catch (e) {
    console.log('script error', e); // eslint-disable-line
  }
};

const messagePugPigApplicationAdIsRendered = () => {
  try {
    window.Messaging.postMessage('onPageReady');
  } catch (e) {
    // do nothing window.Messaging || Messaging.postMessage has not been defined
  }
};

const renderArtboardFonts = () => {
  const { activeArtboard: { fonts }, rootHead } = store.getState();
  appendHtmlToElement(fonts, rootHead);
};

export const initialiseArtboard = (artboard) => {
  const state = store.getState();
  const {
    iabFixed,
    interstitial,
    revealFormat,
    rootElement,
    timelines,
    targetComponentTimelines,
    artboards,
    api,
    canAccessIframe,
    rootWindow,
    safeFrame,
    thirdPartyTracking,
  } = state;
  targetComponentTimelines[artboard.key] = [];
  renderArtboardFonts();
  createButtonEvents(rootElement, artboard, artboards);
  initialiseAnimation(timelines[artboard.key], getFilteredArtboardTimeline(artboard), handleKeyFrameActions);

  initialiseVideoActions();
  initialiseVideoControls();
  [...artboard.layerInfo.imagezoom].forEach(l => createImageZoom(l.id));
  [...artboard.layerInfo.threeSixty].forEach(l => createSpriteSlider(l.id, l.assetURLs));
  [...artboard.layerInfo.swipegallery].forEach(l => createSwipeGallery(l.id, l.assetURLs));
  [...artboard.layerInfo.wipeaway].forEach(l => createWipeAway(l.id));
  [...artboard.layerInfo.effectlayer].forEach(l => createEffectLayer(l.id));

  createSmartGroupEvents(rootElement);
  runScript(api, artboard.script);

  if (thirdPartyTracking.clickthrough) {
    [...rootElement.querySelectorAll(`.${CLICK_THROUGH_BUTTON_CLASS}`)].map((b) => {
      const uid = b.dataset.id;
      if (thirdPartyTracking.clickthrough[uid]) {
        const clickthroughUrl = thirdPartyTracking.clickthrough[uid] || thirdPartyTracking.clickthrough.all;
        const is1x1 = clickthroughUrl.includes('{{1x1}}');
        let adjustedTracking = thirdPartyTracking.clickthrough[uid].replace(REGEX_FOR_CACHEBUSTER, cachebusterFunction());
        adjustedTracking = replaceConsentMacros(adjustedTracking);
        if (is1x1) {
          b.dataset.pixel = unescapeHtml(adjustedTracking);
        } else {
          b.dataset.href = unescapeHtml(adjustedTracking);
        }
      }
    });

    if (iabFixed && !FLUID_PLUS) {
      rootElement.style.width = `${artboard.viewport.width}px`;
      rootElement.style.maxWidth = `${artboard.viewport.width}px`;
      rootElement.style.height = `${artboard.viewport.height}px`;
      rootElement.style.maxHeight = `${artboard.viewport.height}px`;
    }
  }

  if (ENVIRONMENT === 'pugpig') { // eslint-disable-line no-undef
    messagePugPigApplicationAdIsRendered();
  }

  if (revealFormat) {
    const artboardBorder = rootElement.querySelector(`#${ARTBOARD_BORDER_DOM_ID}`);
    const borderTarget = canAccessIframe ? rootWindow.frameElement.parentNode.parentNode.parentNode : rootElement.parentNode.parentNode.parentNode;
    if (canAccessIframe && (borderTarget.nodeName !== '#document')) borderTarget.style.position = 'relative';
    const existingBorder = borderTarget.querySelector(`div > #${ARTBOARD_BORDER_DOM_ID}`);
    if (existingBorder && canAccessIframe) borderTarget.removeChild(existingBorder);
    if (artboardBorder) borderTarget.appendChild(artboardBorder);
  }
  if (interstitial) {
    const adContainer = rootElement.querySelector('#i-a-wrapper div');
    adContainer.style.top = `calc(50% - ${adContainer.clientHeight / 2}px)`;
  }
};

const hideScrollbar = () => {
  const { rootElement, scrollbarWidth } = store.getState();
  rootElement.style.paddingRight = `${scrollbarWidth}px`;
};

const updateRootElementFontSize = () => {
  const { rootElement, activeArtboard: { viewport: { width } } } = store.getState();
  const clientWidth = parseFloat(window.getComputedStyle(rootElement).width);
  rootElement.style.fontSize = `${PARENT_FONT_SIZE_PX * parseFloat(clientWidth / width)}px`;
};

export const getScaledPixelValue = (pixel) => {
  const { rootElement, activeArtboard: { viewport: { width } } } = store.getState();
  const clientWidth = parseFloat(window.getComputedStyle(rootElement).width);
  return pixel * parseFloat(clientWidth / width);
};

export const getScaledArtboardPixelValue = (pixel, container, artboardName) => {
  const { artboards } = store.getState();
  const artboard = artboards.find(a => a.name === artboardName);
  const clientWidth = parseFloat(container.getBoundingClientRect().width);
  return pixel * parseFloat(clientWidth / artboard.viewport.width);
};

const loadExternalScripts = (artboard) => {
  const script = artboard.script;
  if (script.includes('api.loadScript')) {
    const scriptRegex = /loadScript\((?:\"|')(.+?)(?:\"|')\)/gm;
    const scripts = [...script.matchAll(scriptRegex)];
    scripts.forEach((itm) => {
      var preload = document.createElement('link');
      preload.href = itm[1];
      preload.rel = 'preload';
      preload.as = 'script';
      document.head.appendChild(preload);
    });
    initialiseArtboard(artboard);
  } else {
    initialiseArtboard(artboard);
  }
}

export const renderArtboard = () => {
  const { rootElement, activeArtboard, timelines, overlay, overlayElement } = store.getState();
  const timeline = timelines[activeArtboard.key];
  timeline.pause(0);
  updateRootElementFontSize();

  rootElement.innerHTML = activeArtboard.html;
  const isiOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform); // eslint-disable-line no-undef
  if (ENVIRONMENT === 'pugpig') {
    [...rootElement.querySelectorAll('video')].map(e =>
      [...e.querySelectorAll('source[src*="-main.mp4"]')].map(s => e.setAttribute('src', s.src)));
  } else if (isiOS) {
    [...rootElement.querySelectorAll('video')].map((e) => {
      [...e.querySelectorAll('source[src*="m3u8"]')].map(s => e.setAttribute('src', `${s.src}#t=0.001`));
      e.load();
    });
  }
  hideScrollbar();
  imagesLoaded(rootElement, { background: '*' }, loadExternalScripts(activeArtboard));

  initialiseComponentTimelines();
  artboardRendered();
  maybeStartAd(activeArtboard);
  setTimeout(visibilityChangeEvent, 250);
};

export const defaultArtboards = artboards => artboards.filter(a => ('html' in a) && (a.html.indexOf(`${CLOSE_EXPAND_BUTTON_CLASS}`) === -1));

export const expandArtboards = artboards => artboards.filter(a => ('html' in a) && (a.html.indexOf(`${CLOSE_EXPAND_BUTTON_CLASS}`) !== -1));

const checkExpanded = () => {
  const { baseElement, expand, pushdown, skins, overlay } = store.getState();
  return (expand || pushdown || skins || overlay) && (baseElement.getAttribute('data-state') === 'expanded' || baseElement.getAttribute('data-state') === 'overlay');
};

export const selectArtboard = (artboards, { 0: containerWidth, 1: containerHeight }) => {
  const { inline, pushdown, ribbon, revealFormat, skins, wallpaper, rootBodyElement, rootWindow } = store.getState();

  if (artboards.length === 0) return;
  const widthBasedCalc = inline || pushdown || ribbon || revealFormat || skins || wallpaper;
  const viewportRatio = containerWidth / containerHeight;
  const detectionWidth = wallpaper ? (window.top.innerWidth / (rootBodyElement.getBoundingClientRect().width / 1000)) : containerWidth;

  const filteredArtboards = checkExpanded() ? expandArtboards(artboards) : defaultArtboards(artboards);
  const sortableArtboards = filteredArtboards.length ? filteredArtboards : artboards;
  return sortableArtboards.map((artboard) => {
    const artboardViewport = artboard.viewport;
    const artboardWidth = artboardViewport.width;
    const artboardHeight = artboardViewport.height;
    const artboardRatio = artboardWidth / artboardHeight;

    const xDifference = artboardWidth - detectionWidth;
    const yDifference = artboardHeight - containerHeight;

    const sortedRatio = [viewportRatio, artboardRatio].sort((a, b) => a - b);
    const newRatio = sortedRatio[1] / sortedRatio[0];

    const differenceTotal = (Math.abs(xDifference) + Math.abs(yDifference));
    const difference = (widthBasedCalc) ? Math.abs(xDifference) : differenceTotal;
    const weight = (widthBasedCalc) ? difference : difference * newRatio;

    if (skins || wallpaper) {
      if (xDifference >= 0) return { artboard, weight };
      if (['mobile', 'center-', 'centre-', '-large', '-lg'].some(n => artboard.name.toLowerCase().includes(n))) return { artboard, weight };
    } else {
      return { artboard, weight };
    }
  }).sort((a, b) => a.weight - b.weight)[0].artboard;
};

const getContainerSize = (state) => {
  const { rootBodyElement, wallpaper } = store.getState();
  if (ENVIRONMENT === 'pugpig') { // eslint-disable-line no-undef
    const { rootWindow: { screen: rootScreen } } = state;
    const screenSize = [rootScreen.width, rootScreen.height];
    return screenSize;
  }
  if (wallpaper && !IS_PREVIEW_PAGE) {
    const rootWindow = window.top;
    const overlap = parseInt(rootBodyElement.getAttribute('data-ivo-ol'), 10) || 100;
    return [rootWindow.innerWidth + overlap, rootWindow.innerHeight];
  }
  const {
    rootElement: {
      clientWidth = 0,
      clientHeight = 0,
    },
  } = state;
  return [clientWidth, clientHeight];
};

export const arePreviewArtboardsLoaded = () => {
  if (frameElement) {
    const rootWindow = window.top;
    const rootDocument = rootWindow.document;
    const previewArtboards = rootDocument.querySelectorAll('[data-preview-artboard]');
    const previewArtboardsLoaded = rootDocument.querySelectorAll('[data-preview-artboard-loaded]');
    return previewArtboards.length === previewArtboardsLoaded.length;
  }
  return true;
};

const previewArtboardLoaded = () => {
  const rootWindow = window.top;
  const rootDocument = rootWindow.document;
  if (frameElement) {
    frameElement.setAttribute('data-preview-artboard-loaded', '');
    const event = new Event('preview-artboard-loaded');
    window.top.dispatchEvent(event);
    const previewArtboards = rootDocument.querySelectorAll('[data-preview-artboard]');
    [...previewArtboards].forEach((previewArtboard) => {
      previewArtboard.contentWindow.dispatchEvent(event);
    });
  }
  const previewsLoaded = arePreviewArtboardsLoaded();
  if (previewsLoaded) renderArtboard();
};

export const maybeChangeArtboard = () => {
  const { scroller } = store.getState();
  if (scroller && !IS_PREVIEW_PAGE) { resetScrollerAdContainer(); }

  const {
    artboards,
    activeArtboard: currentArtboard = false,
    baseElement,
    rootElement,
    rootElement: {
      clientWidth = 0,
      clientHeight = 0,
    },
    timelines,
    canAccessIframe,
    safeFrame,
    fluidPlus,
    sessionId,
    expand,
    inline,
    overlay,
    wallpaper,
    pushdown,
    ribbon,
    formatName,
    placementId,
  } = store.getState();

  const containerSize = getContainerSize(store.getState());
  const outstream = formatName.toLowerCase().indexOf('outstream') !== -1;
  const previewArtboard = frameElement ? frameElement.getAttribute('data-preview-artboard') : null;
  const dataState = baseElement.getAttribute('data-state');
  const isOverlayState = (dataState === 'overlay');
  const artboardAttribute = rootElement.parentNode ? rootElement.parentNode.getAttribute('data-artboard') : null;
  const dataArtboard = isOverlayState ? null : artboardAttribute || previewArtboard || null;
  let activeArtboard;

  if (containerSize[0] > 0 && containerSize[1] > 0) {
    if (dataArtboard) {
      activeArtboard = artboards.find(a => a.name === dataArtboard);
    } else {
      activeArtboard = selectArtboard(artboards, containerSize);
    }

    setTimeline(timelines[activeArtboard.key]);

    if ((currentArtboard === activeArtboard)) {
      updateRootElementFontSize();
      if (fluidPlus && !scroller && !outstream) {
        resizeWhiteSpaceOfParents(safeFrame, canAccessIframe);
      }
      return;
    }

    selectActiveArtboard(activeArtboard);
    fireTracking(AD_STATE_CHANGED);

    if (IS_PREVIEW_PAGE) {
      previewArtboardLoaded();
    } else {
      renderArtboard();
    }

    if (scroller) resizeScrollerContainer();
    if (pushdown) resizePushdown(activeArtboard);
    if (dataState === 'overlay') {
      rootElement.parentNode.setAttribute('data-artboard', activeArtboard.name);
      resizeOverlayContainer();
      initialiseDocumentEventListeners(rootElement);
    }
    if (canAccessIframe && !IS_PREVIEW_PAGE) {
      if (fluidPlus && !scroller && !outstream) resizeWhiteSpaceOfParents(safeFrame, canAccessIframe);
      if (!fluidPlus && !safeFrame && inline) resizeIframe(activeArtboard);
      if (expand) resizeIframe(activeArtboard, '');
      if (outstream) resizeOutstreamViewport(sessionId, activeArtboard, safeFrame, canAccessIframe);
      if (wallpaper) resizeWallpaperWrapper(activeArtboard);
      if (ribbon) resizeRibbonContainer(sessionId, activeArtboard, safeFrame, canAccessIframe);
    } else {
      if (canAccessIframe && wallpaper) {
        frameElement.parentNode.parentNode.parentNode.style.zIndex = '-1';
        resizePreviewWallpaperWrapper();
      }
    }
  }
};

export const maybeRenderArtboard = () => {
  if (arePreviewArtboardsLoaded()) renderArtboard();
};

export const setArtboard = (artboard, eventType) => {
  const state = store.getState();
  const {
    rootElement,
    canAccessIframe,
    mraid,
    pushdown,
    skins,
    ribbon,
  } = state;

  const shoudSetIframe = (canAccessIframe || mraid) && !skins && !ribbon;

  if ((eventType === 'expand' || eventType === 'expand-close' || eventType === 'close' || eventType === 'switch-artboard') && shoudSetIframe) {
    if (pushdown) {
      pushdownIframe(artboard, eventType);
    } else {
      resizeIframe(artboard, eventType);
    }
  }

  if ((eventType === 'expand' || eventType === 'expand-close' || eventType === 'switch-artboard') && canAccessIframe && !ribbon && skins) {
    resizeSkinsCentreContainer(artboard, eventType);
  }

  if ((eventType === 'expand-close') && canAccessIframe && !skins && ribbon) {
    rootElement.ownerDocument.defaultView.frameElement.parentNode.style.transition = 'top 0.2s, bottom 0.2s';
  }

  rootElement.parentNode.setAttribute('data-artboard', artboard.name);

  if (IS_PREVIEW_PAGE && !canAccessIframe) {
    const event = new CustomEvent('artboard-change');
    document.dispatchEvent(event);
  } else {
    maybeChangeArtboard();
  }
};



// WEBPACK FOOTER //
// ./app/javascript/renderer/domOperations/artboardOperations.js